import React, { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, ArrowUp, ArrowDown, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const SECTION_TYPES = [
  { type: "hero", label: "Hero image" },
  { type: "heading", label: "Heading" },
  { type: "text", label: "Text" },
  { type: "image", label: "Image" },
  { type: "button", label: "Button" },
];

const uid = () => Math.random().toString(36).slice(2, 10);
const newSection = (type) => ({ id: uid(), type, heading: "", text: "", image_url: "", button_label: "", button_url: "", align: "left" });

const inputCls = "w-full bg-transparent border border-ink/15 rounded-md py-2 px-3 text-ink text-sm focus:outline-none focus:border-trailride transition-colors";

function SectionEditor({ section, onChange, onMoveUp, onMoveDown, onRemove, index, total }) {
  const set = (k) => (e) => onChange({ ...section, [k]: e.target.value });
  const label = SECTION_TYPES.find((t) => t.type === section.type)?.label || section.type;
  return (
    <div className="border border-ink/15 rounded-md p-4" data-testid={`section-editor-${index}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="font-sans text-xs font-semibold tracking-wide uppercase text-trailride">{label}</span>
        <div className="flex items-center gap-1">
          <button type="button" onClick={onMoveUp} disabled={index === 0} className="p-1.5 rounded hover:bg-ink/5 disabled:opacity-30" title="Move up"><ArrowUp size={14} /></button>
          <button type="button" onClick={onMoveDown} disabled={index === total - 1} className="p-1.5 rounded hover:bg-ink/5 disabled:opacity-30" title="Move down"><ArrowDown size={14} /></button>
          <button type="button" onClick={onRemove} data-testid={`section-remove-${index}`} className="p-1.5 rounded hover:bg-red-50 text-red-500" title="Remove"><Trash2 size={14} /></button>
        </div>
      </div>
      <div className="space-y-3">
        {(section.type === "hero" || section.type === "image") && (
          <input value={section.image_url} onChange={set("image_url")} placeholder="Image URL" data-testid={`section-image-${index}`} className={inputCls} />
        )}
        {(section.type === "hero" || section.type === "heading") && (
          <input value={section.heading} onChange={set("heading")} placeholder="Heading text" data-testid={`section-heading-${index}`} className={inputCls} />
        )}
        {section.type === "text" && (
          <textarea rows={4} value={section.text} onChange={set("text")} placeholder="Paragraph text" data-testid={`section-text-${index}`} className={inputCls} />
        )}
        {section.type === "button" && (
          <div className="grid grid-cols-2 gap-3">
            <input value={section.button_label} onChange={set("button_label")} placeholder="Button label" data-testid={`section-btnlabel-${index}`} className={inputCls} />
            <input value={section.button_url} onChange={set("button_url")} placeholder="Button URL (/events or https://…)" data-testid={`section-btnurl-${index}`} className={inputCls} />
          </div>
        )}
        {section.type !== "image" && (
          <select value={section.align} onChange={set("align")} className={inputCls}>
            <option value="left">Align left</option>
            <option value="center">Align center</option>
          </select>
        )}
      </div>
    </div>
  );
}

function PageEditor({ initial, onClose, onSaved }) {
  const editing = !!initial?.id;
  const [title, setTitle] = useState(initial?.title || "");
  const [slug, setSlug] = useState(initial?.slug || "");
  const [status, setStatus] = useState(initial?.status || "published");
  const [showInNav, setShowInNav] = useState(initial?.show_in_nav || false);
  const [sections, setSections] = useState(initial?.sections?.length ? initial.sections.map((s) => ({ ...s, id: s.id || uid() })) : []);
  const [saving, setSaving] = useState(false);

  const updateSection = (i, val) => setSections((arr) => arr.map((s, idx) => (idx === i ? val : s)));
  const removeSection = (i) => setSections((arr) => arr.filter((_, idx) => idx !== i));
  const move = (i, dir) => setSections((arr) => {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return arr;
    const copy = [...arr];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { title, slug: slug || title, status, show_in_nav: showInNav, sections };
    try {
      if (editing) await api.put(`/admin/pages/${initial.id}`, payload);
      else await api.post("/admin/pages", payload);
      toast.success(editing ? "Page updated" : "Page created");
      onSaved();
      onClose();
    } catch {
      toast.error("Could not save page");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-ink/40 flex items-start justify-center overflow-y-auto p-4" data-testid="page-editor">
      <div className="bg-paper w-full max-w-3xl rounded-lg my-8 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink/10 sticky top-0 bg-paper rounded-t-lg">
          <h3 className="font-serif text-xl font-bold text-ink">{editing ? "Edit page" : "New page"}</h3>
          <button onClick={onClose} aria-label="Close" data-testid="page-editor-close" className="text-ink/50 hover:text-ink"><X size={20} /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="font-sans text-xs text-ink/60">Title</label>
              <input required value={title} onChange={(e) => setTitle(e.target.value)} data-testid="page-title" className={inputCls} />
            </div>
            <div>
              <label className="font-sans text-xs text-ink/60">URL slug (optional)</label>
              <input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="auto from title" data-testid="page-slug" className={inputCls} />
            </div>
            <div>
              <label className="font-sans text-xs text-ink/60">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="page-status" className={inputCls}>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
            </div>
            <label className="flex items-center gap-2 font-sans text-sm text-ink mt-6 cursor-pointer">
              <input type="checkbox" checked={showInNav} onChange={(e) => setShowInNav(e.target.checked)} data-testid="page-shownav" className="accent-[#4B1B68] w-4 h-4" />
              Show in top navigation
            </label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="font-sans text-sm font-semibold text-ink">Sections</label>
            </div>
            <div className="space-y-3">
              {sections.length === 0 && <p className="font-sans text-sm text-ink/40">No sections yet. Add a block below.</p>}
              {sections.map((s, i) => (
                <SectionEditor
                  key={s.id}
                  section={s}
                  index={i}
                  total={sections.length}
                  onChange={(v) => updateSection(i, v)}
                  onMoveUp={() => move(i, -1)}
                  onMoveDown={() => move(i, 1)}
                  onRemove={() => removeSection(i)}
                />
              ))}
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              {SECTION_TYPES.map((t) => (
                <button
                  key={t.type}
                  type="button"
                  onClick={() => setSections((arr) => [...arr, newSection(t.type)])}
                  data-testid={`add-section-${t.type}`}
                  className="inline-flex items-center gap-1.5 font-sans text-xs font-medium border border-ink/20 rounded-full px-3 py-1.5 text-ink hover:border-trailride hover:text-trailride transition-colors"
                >
                  <Plus size={13} /> {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-ink/10">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full border border-ink/20 font-sans text-sm text-ink hover:bg-ink/5">Cancel</button>
            <button type="submit" disabled={saving} data-testid="page-save" className="px-6 py-2.5 rounded-full bg-trailride text-white font-sans text-sm font-medium hover:bg-trailride-dark disabled:opacity-60">
              {saving ? "Saving…" : "Save page"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function PagesTab() {
  const [pages, setPages] = useState([]);
  const [editor, setEditor] = useState(null);
  const load = useCallback(() => api.get("/admin/pages").then((r) => setPages(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await api.delete(`/admin/pages/${p.id}`); toast.success("Page deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-pages-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-2xl font-bold text-ink">Custom pages</h2>
          <p className="font-sans text-sm text-ink/55 mt-1">Build stackable pages and optionally show them in the navigation.</p>
        </div>
        <button onClick={() => setEditor({})} data-testid="admin-new-page" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-trailride text-white font-sans text-sm font-medium hover:bg-trailride-dark">
          <Plus size={16} /> New page
        </button>
      </div>

      <div className="border border-ink/10 rounded-lg divide-y divide-ink/10">
        {pages.length === 0 && <p className="p-6 font-sans text-ink/50">No custom pages yet.</p>}
        {pages.map((p) => (
          <div key={p.id} className="p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3" data-testid={`admin-page-row-${p.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-sans font-semibold text-ink truncate">{p.title}</span>
                <span className={`font-sans text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${p.status === "published" ? "bg-trailride text-white" : "bg-ink/10 text-ink/70"}`}>{p.status}</span>
                {p.show_in_nav && <span className="font-sans text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-ink/15 text-ink/60">In nav</span>}
              </div>
              <a href={`/p/${p.slug}`} target="_blank" rel="noreferrer" className="font-sans text-sm text-ink/50 hover:text-trailride inline-flex items-center gap-1 mt-1">/p/{p.slug} <ExternalLink size={12} /></a>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setEditor(p)} data-testid={`admin-edit-page-${p.id}`} className="p-2 rounded-md hover:bg-ink/5 text-ink/60"><Pencil size={16} /></button>
              <button onClick={() => remove(p)} data-testid={`admin-delete-page-${p.id}`} className="p-2 rounded-md hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      {editor && <PageEditor initial={editor.id ? editor : null} onClose={() => setEditor(null)} onSaved={load} />}
    </div>
  );
}
